// Path-based router for a single-page application served from
// Cloudflare Pages.
//
// Pages handles SPA fall-through through public/_redirects ("/*
// /index.html 200"), which means deep links like
// /governance/authority-register resolve to the same bundle and the
// client-side router parses location.pathname to choose the active
// view. Internal links use <a href="..."> wrapped by handleNav so the
// browser does not request a fresh document.

export type RouteName =
  | 'dashboard'
  | 'catalogue'
  | 'changes'
  | 'history'
  | 'governance'
  | 'guide'
  | 'not_found';

export interface ParsedRoute {
  name: RouteName;
  path: string;
  params: Record<string, string>;
  query: URLSearchParams;
}

function parsePath(path: string, query: URLSearchParams): ParsedRoute {
  if (path === '/' || path === '') return { name: 'dashboard', path, params: {}, query };
  const segments = path.replace(/^\/|\/$/g, '').split('/');
  const head = segments[0];

  switch (head) {
    case 'dashboard':
      return { name: 'dashboard', path, params: {}, query };
    case 'catalogue': {
      const params: Record<string, string> = {};
      if (segments[1]) params.mode = segments[1];
      // Mode-specific segments. matrix takes a sub-tab name; detail
      // takes jurisdiction + product. comparison uses query params
      // (?left, ?right) and consumes no path segments beyond the mode.
      if (segments[1] === 'matrix') {
        if (segments[2]) params.subtab = segments[2];
      } else if (segments[1] === 'detail') {
        if (segments[2]) params.jurisdiction = segments[2];
        if (segments[3]) params.product = segments[3];
      }
      return { name: 'catalogue', path, params, query };
    }
    case 'changes': {
      const params: Record<string, string> = {};
      if (segments[1]) params.tab = segments[1];
      if (segments[2]) params.reference = segments[2];
      // /changes/<tab>/<reference>/<action> — currently only the
      // "revise" action is used, opening the cube editor in revision
      // mode for an open proposal. Treated as an opt-in third segment
      // so legacy two-segment URLs continue to render the review
      // interface unchanged.
      if (segments[3]) params.action = segments[3];
      return { name: 'changes', path, params, query };
    }
    case 'history': {
      const params: Record<string, string> = {};
      if (segments[1]) params.jurisdiction = segments[1];
      if (segments[2]) params.product = segments[2];
      return { name: 'history', path, params, query };
    }
    case 'governance': {
      const params: Record<string, string> = {};
      if (segments[1]) params.panel = segments[1];
      return { name: 'governance', path, params, query };
    }
    case 'guide': {
      const params: Record<string, string> = {};
      // /guide                  → landing (sectioned list)
      // /guide/<slug>           → specific walkthrough
      if (segments[1]) params.slug = segments[1];
      return { name: 'guide', path, params, query };
    }
    default:
      return { name: 'not_found', path, params: {}, query };
  }
}

const routeState = $state<{ current: ParsedRoute }>({
  current: { name: 'dashboard', path: '/', params: {}, query: new URLSearchParams() }
});

export const route = routeState;

function syncFromLocation(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  routeState.current = parsePath(url.pathname, url.searchParams);
}

export function initRouter(): void {
  if (typeof window === 'undefined') return;
  syncFromLocation();
  window.addEventListener('popstate', syncFromLocation);
  // Capture <a> clicks so deep links update the URL without a full reload.
  window.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('http') || href.startsWith('mailto:') || anchor.target === '_blank') return;
    if (anchor.hasAttribute('data-external')) return;
    event.preventDefault();
    navigate(href);
  });
}

export function navigate(href: string, options: { replace?: boolean } = {}): void {
  if (typeof window === 'undefined') return;
  const url = new URL(href, window.location.origin);
  const newPath = url.pathname + url.search;
  if (options.replace) {
    window.history.replaceState({}, '', newPath);
  } else {
    window.history.pushState({}, '', newPath);
  }
  routeState.current = parsePath(url.pathname, url.searchParams);
}

export function isActive(prefix: string): boolean {
  if (prefix === '/') return routeState.current.path === '/' || routeState.current.path === '/dashboard';
  return routeState.current.path === prefix || routeState.current.path.startsWith(prefix + '/');
}

// Exported for unit tests.
export const __testing = { parsePath };
